import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: custom * 0.1, duration: 0.5 }
    })
  };

  return (
    <section className="py-12 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <motion.h2 
          className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-10 text-center"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t('contact.title', 'Kontakt')}
        </motion.h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Primary Contact Information */}
          <motion.div 
            className="bg-white p-6 rounded-lg shadow-md"
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariants}
            whileHover={{ y: -5, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }}
          >
            <h3 className="text-xl font-semibold mb-4">{t('contact.contact_info', 'Dane kontaktowe')}</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700">{t('contact.address', 'Adres:')}</h4>
                <p className="text-gray-600">{t('contact.address_value', 'ul. Stanisława Staszica Stanica WOPR, 64-800 Chodzież')}</p>
              </div>

              <div>
                <h4 className="font-medium text-gray-700">{t('contact.mailing_address', 'Adres korespondencyjny:')}</h4>
                <p className="text-gray-600">{t('contact.mailing_address_value', 'skrytka pocztowa 18, 64-800 Chodzież')}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700">{t('contact.email', 'Email:')}</h4>
                <a href="mailto:chodzieskiewopr@wp.pl" className="text-blue-600 hover:text-blue-700 transition-colors">
                  chodzieskiewopr@wp.pl
                </a>
              </div>
              

              <div>
                <h4 className="font-medium text-gray-700">{t('contact.social_media', 'Media społecznościowe:')}</h4>
                <div className="flex space-x-4 mt-2">
                  <a 
                    href="https://www.facebook.com/woprchodziez" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition transform hover:scale-110"
                  >
                    <img 
                      src={`${process.env.PUBLIC_URL}/images/icons/fb_icon.svg`} 
                      alt="Facebook" 
                      className="w-8 h-8"
                    />
                  </a>
                  <a 
                    href="https://www.instagram.com/chodzieskiewopr/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transition transform hover:scale-110"
                  >
                    <img 
                      src={`${process.env.PUBLIC_URL}/images/icons/instagram_icon.svg`} 
                      alt="Instagram" 
                      className="w-8 h-8"
                    />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Additional Information */}
          <motion.div 
            className="bg-white p-6 rounded-lg shadow-md"
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariants}
            whileHover={{ y: -5, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }}
          >
            <h3 className="text-xl font-semibold mb-4">{t('contact.org_info', 'Informacje o organizacji')}</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700">NIP:</h4>
                <p className="text-gray-600">6070066740</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700">REGON:</h4>
                <p className="text-gray-600">301469936</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700">KRS:</h4>
                <p className="text-gray-600">0000359859</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700">{t('contact.bank_account', 'Konto bankowe:')}</h4>
                <p className="text-gray-600">ERSTE<br />23 1090 1317 0000 0001 1437 2833</p>
              </div>
            </div>
          </motion.div>

          {/* Direct Contact */}
          <motion.div 
            className="bg-white p-6 rounded-lg shadow-md"
            custom={2}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariants}
            whileHover={{ y: -5, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }}
          >
            <h3 className="text-xl font-semibold mb-4">{t('contact.direct_contact', 'Kontakt bezpośredni')}</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-700">Maciej Bielecki</h4>
                <p className="text-gray-600 text-sm">{t('board.roles.president', 'Prezes Zarządu Chodzieskiego WOPR')}</p>
                <a href="tel:+48504679470" className="text-blue-600 hover:text-blue-700 transition-colors text-sm">
                  +48 504 679 470
                </a>
              </div>

              <div>
                <h4 className="font-medium text-gray-700">Weronika Nowak</h4>
                <p className="text-gray-600 text-sm">{t('board.roles.secretary', 'Sekretarz Zarządu Chodzieskiego WOPR')}</p>
                <a href="tel:+48882812945" className="text-blue-600 hover:text-blue-700 transition-colors text-sm">
                  +48 882 812 945
                </a>
              </div>

              <div>
                <h4 className="font-medium text-gray-700">Krzysztof Majewski</h4>
                <p className="text-gray-600 text-sm">{t('board.roles.treasurer', 'Skarbnik Zarządu Chodzieskiego WOPR')}</p>
                <a href="tel:+48600930952" className="text-blue-600 hover:text-blue-700 transition-colors text-sm">
                  +48 600 930 952
                </a>
              </div>
            </div>
          </motion.div>

          
        </div>
        
        {/* Map Section */}
        <motion.div 
          className="mt-8"
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={cardVariants}
        >
          <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
            <h3 className="text-xl font-semibold mb-4">{t('contact.location', 'Nasza lokalizacja')}</h3>
            <div className="max-w-3xl mx-auto">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1815.0550469713123!2d16.92560660550505!3d52.99135604828571!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4703f3ba4d2abae7%3A0x6191ce550aee7224!2sSTANICA%20Odzia%C5%82u%20Miejskiego%20WOPR!5e0!3m2!1spl!2spl!4v1752523333570!5m2!1spl!2spl"
                width="100%" 
                height="300" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
                title="Mapa lokalizacji WOPR Chodzież"
              ></iframe>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
